import {
  DialogContent,
  Typography,
  TextField,
  Stack,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Box,
  Divider,
  Switch, // اضافه شده برای کنترل وضعیت محاسباتی جدید
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { NumericFormat } from 'react-number-format';
import { keyframes } from '@mui/system';
import { useDialogContext } from '../dialog-context';
import { useToastContext } from '../toast-context';
import ResponsiveDialogActions from '../ResponsiveDialogAction';
import { cutDigitFromRight, isNumeric } from '../../utils';
import { services } from '../../pages/Sell/services';
import ConfirmFloorPriceDialog from '../confirm-floor-price-dialog'; // ایمپورت دایلاگ تایید مجزا

export const spin = keyframes`
  from {
    opacity: 0;
    transform: translateY(-20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const strategies = {
  AUTOMATIC_ESTJT: 'مرجع قیمت',
  AUTOMATIC_COMPETITOR: 'رقیب',
  MANUAL: 'دستی',
};

const getLocalProfitPercentage = (bullionData, strategy) => {
  let profitPercentage = null;
  let profitPercentageVariable = null;

  if (strategy === 'MANUAL') {
    profitPercentage = bullionData.manualMarginPercentage;
    profitPercentageVariable = 'manualMarginPercentage';
  } else if (strategy === 'AUTOMATIC_COMPETITOR') {
    profitPercentage = bullionData.differenceWithCompetitorPercentage;
    profitPercentageVariable = 'differenceWithCompetitorPercentage';
  } else if (strategy === 'AUTOMATIC_ESTJT') {
    profitPercentage = bullionData.estjtProfitPercentage;
    profitPercentageVariable = 'estjtProfitPercentage';
  }

  return { profitPercentage, profitPercentageVariable };
};

// --- PREMIUM UI STYLES (COMPACT) ---
const premiumInputSx = {
  direction: 'ltr',
  '& .MuiOutlinedInput-root': {
    borderRadius: '12px',
    backgroundColor: '#F1F5F9', // Subtle modern gray
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    '& fieldset': {
      border: 'none', // Remove default harsh border
    },
    '&:hover': {
      backgroundColor: '#E2E8F0',
    },
    '&.Mui-focused': {
      backgroundColor: '#FFFFFF',
      boxShadow:
        '0 0 0 3px rgba(16, 185, 129, 0.2), 0 4px 12px rgba(0,0,0,0.06)',
    },
    '& .MuiInputBase-input': {
      fontWeight: 700,
      color: '#0F172A',
      fontSize: '0.95rem',
      padding: '10px 14px', // Tighter padding
    },
  },
  '& .MuiInputLabel-root': {
    color: '#64748B',
    fontWeight: 600,
    fontSize: '0.85rem',
    '&.Mui-focused': { color: '#10B981', fontWeight: 700 },
  },
  '& .MuiFormHelperText-root': {
    color: '#64748B',
    fontWeight: 500,
    mt: 0.5,
    fontSize: '0.75rem',
    textAlign: 'end',
  },
};
// --------------------------

export function PriceDialog({
  is995,
  isPreview,
  bullionWeight,
  manualBasePriceDefault,
  trig,
  trigger,
  costPercentageDefault,
  transactionType,
  onClick,
  profitPercentageDefault,
  cutNumberDefault,
  priceDefault,
  strategyDefault,
  boardPriceDefault,
  bullionData,
  calculateFloorPriceForSellDefault, // اضافه شدن پراپ پیش‌فرض وضعیت جدید
}) {
  const [estjPrices, setEstjPrices] = React.useState();
  const [competitorPrice, setCompetitorPrice] = useState(null);
  const [openFloorPriceConfirm, setOpenFloorPriceConfirm] = useState(false);

  const {
    control,
    handleSubmit,
    watch,
    setValue,
    register,
    formState: { isSubmitting, isDirty },
  } = useForm({
    defaultValues: {
      costPercentage: costPercentageDefault,
      // همگام‌سازی مقدار اولیه با پراپ پیش‌فرض ارسالی
      calculateFloorPriceForSell: !!calculateFloorPriceForSellDefault,
    },
  });
  const formValues = watch();
  const strategy = formValues.strategy || strategyDefault;
  const manualBasePrice = !!String(formValues?.manualBasePrice)?.includes(',')
    ? Number(formValues?.manualBasePrice?.replace(/,/g, ''))
    : Number(formValues?.manualBasePrice) || manualBasePriceDefault;
  const costPercentage = Number(formValues.costPercentage);

  useEffect(() => {
    services.getEstjPrices().then(prices => {
      setEstjPrices(prices);
    });
  }, [trigger]);

  useEffect(() => {
    services.getCompetitorPrices().then(data => setCompetitorPrice(data));
  }, [trigger]);

  useEffect(() => {
    const fetchEstjPrices = () => {
      services.getEstjPrices().then(prices => {
        setEstjPrices(prices);
      });
    };

    fetchEstjPrices();

    const interval = setInterval(fetchEstjPrices, 20000);

    return () => clearInterval(interval);
  }, []);

  const pricePerGram = estjPrices?.gold24k * 10;
  const isCompetitorStrategy = strategy === 'AUTOMATIC_COMPETITOR';
  const isESTJTStrategy = strategy === 'AUTOMATIC_ESTJT';

  const isManualStrategy = strategy === 'MANUAL';
  const marginPercentages = {
    AUTOMATIC_ESTJT: {
      value: 'estjtProfitPercentage',
      label: 'درصد حاشیه با مرجع',
    },
    AUTOMATIC_COMPETITOR: {
      value: 'differenceWithCompetitorPercentage',
      label: 'درصد حاشیه با رقیب',
    },
    MANUAL: { value: 'manualMarginPercentage', label: 'درصد سود' },
  };

  const estjtPricePerGram = !is995 ? pricePerGram : (pricePerGram / 999) * 995;

  const basePrice = isManualStrategy
    ? !is995
      ? manualBasePrice
      : (manualBasePrice / 999) * 995
    : isCompetitorStrategy
      ? competitorPrice?.[bullionWeight]?.tokeniko
      : estjtPricePerGram * bullionWeight;

  const { closeDialog } = useDialogContext();
  const [percentageState, setPercentageState] = useState(
    profitPercentageDefault
  );
  const [appliedCutNumber, setAppliedCutNumber] = useState(cutNumberDefault);
  const [cutDigitBullionPrice, setCutDigitBullionPrice] = useState(0);
  const [activeInput, setActiveInput] = useState(null);
  const [priceWithoutCutNumber, setPriceWithoutCutNumber] = useState(null);
  const [
    previousCalculatedNewBullionPrice,
    setPreviousCalculatedNewBullionPrice,
  ] = useState();

  const manualPriceLessThanThreshhold = false;
  // (!!String(cutDigitBullionPrice)?.includes(',')
  //   ? Number(cutDigitBullionPrice?.replace(/,/g, ''))
  //   : Number(cutDigitBullionPrice)) <=
  // Number(pricePerGram) * bullionWeight * 0.99;
  const manualPriceMoreThanThreshhold = false;
  // (!!String(cutDigitBullionPrice)?.includes(',')
  //   ? Number(cutDigitBullionPrice?.replace(/,/g, ''))
  //   : Number(cutDigitBullionPrice)) >=
  // Number(pricePerGram) * bullionWeight * 1.25;

  const percentageInputName = isPreview
    ? 'manualMarginPercentage'
    : marginPercentages[strategy]?.value || '';

  const newBullionPrice = !!String(formValues?.manualBasePrice)?.includes(',')
    ? Number(formValues?.manualBasePrice?.replace(/,/g, ''))
    : Number(formValues?.manualBasePrice);

  const newBullionBoardPrice = !!String(formValues?.boardPrice)?.includes(',')
    ? Number(formValues?.boardPrice?.replace(/,/g, ''))
    : Number(formValues?.boardPrice);

  const zeroFreePercentage =
    Math.ceil(
      (newBullionPrice /
        Number(basePrice * (!isManualStrategy ? 1 : bullionWeight)) -
        1) *
        100 *
        1000
    ) / 1000;

  const nominalPercentage =
    Math.ceil(
      ((newBullionPrice /
        Number(basePrice * (!isManualStrategy ? 1 : bullionWeight)) -
        1) *
        100 -
        Number(!!costPercentage && isESTJTStrategy ? costPercentage : 0)) *
        1000
    ) / 1000;

  const calculatedNewBullionPrice =
    basePrice *
    (!isManualStrategy ? 1 : bullionWeight) *
    (1 + zeroFreePercentage / 100);
  useEffect(() => {
    setCutDigitBullionPrice(priceDefault);
    setPriceWithoutCutNumber(priceDefault);
  }, [priceDefault]);

  useEffect(() => {
    if (isManualStrategy) {
      if (percentageState < 0) {
        const boardPrice =
          Number(basePrice) * (!isManualStrategy ? 1 : bullionWeight);

        setValue(
          'boardPrice',
          cutDigitFromRight(
            boardPrice,
            !!formValues?.cutNumber ? formValues?.cutNumber : cutNumberDefault
          )
        );
      } else if (percentageState >= 0) {
        setValue('boardPrice', Number(cutDigitBullionPrice));
      }
    }
  }, [
    costPercentage,
    basePrice,
    isManualStrategy,
    bullionWeight,
    percentageState,
    cutNumberDefault,
    formValues?.cutNumber,
  ]);

  useEffect(() => {
    const cutNumber = !!formValues?.cutNumber
      ? formValues?.cutNumber
      : cutNumberDefault;

    setAppliedCutNumber(cutNumber);

    if (activeInput === percentageInputName) {
      setPercentageState(formValues?.[percentageInputName]);

      const percentageSum =
        !!costPercentage && isESTJTStrategy
          ? Number(formValues?.[percentageInputName]) + Number(costPercentage)
          : Number(formValues?.[percentageInputName]);
      const priceWithPercentage =
        Number(basePrice) *
        (!isManualStrategy ? 1 : bullionWeight) *
        (1 + percentageSum / 100);

      setPriceWithoutCutNumber(priceWithPercentage);

      const finalPrice = cutDigitFromRight(
        priceWithPercentage,
        !!formValues?.cutNumber ? formValues?.cutNumber : cutNumberDefault
      );

      setValue('price', finalPrice);
      setCutDigitBullionPrice(finalPrice);
    }

    if (activeInput === 'cutNumber') {
      const percentageSum =
        !!costPercentage && isESTJTStrategy
          ? Number(formValues?.[percentageInputName]) + Number(costPercentage)
          : Number(formValues?.[percentageInputName]);
      const priceWithPercentage =
        Number(basePrice) *
        (!isManualStrategy ? 1 : bullionWeight) *
        (1 + percentageSum / 100);

      setCutDigitBullionPrice(
        cutDigitFromRight(
          Number(priceWithPercentage),
          !!formValues?.cutNumber ? formValues?.cutNumber : cutNumberDefault
        )
      );
    }

    if (activeInput === 'manualBasePrice') {
      setPriceWithoutCutNumber(calculatedNewBullionPrice);

      const percentageSum =
        !!costPercentage && isESTJTStrategy
          ? Number(formValues?.[percentageInputName]) + Number(costPercentage)
          : Number(formValues?.[percentageInputName]);
      const priceWithPercentage =
        Number(basePrice) *
        (!isManualStrategy ? 1 : bullionWeight) *
        (1 + percentageSum / 100);

      setCutDigitBullionPrice(
        cutDigitFromRight(
          priceWithPercentage,
          !!formValues?.cutNumber ? formValues?.cutNumber : cutNumberDefault
        )
      );
    }

    if (activeInput === 'strategy') {
      const percentageInputName = isPreview
        ? 'manualMarginPercentage'
        : marginPercentages[strategy]?.value;

      const { profitPercentage } = getLocalProfitPercentage(
        bullionData,
        strategy
      );

      const percentageSum =
        !!costPercentage && isESTJTStrategy
          ? Number(profitPercentage) + Number(costPercentage)
          : Number(profitPercentage);

      setValue(percentageInputName, profitPercentage);
      setPercentageState(profitPercentage);

      const basePrice = isManualStrategy
        ? !is995
          ? manualBasePrice * bullionWeight
          : ((manualBasePrice * bullionWeight) / 999) * 995
        : isCompetitorStrategy
          ? competitorPrice?.[bullionWeight]?.tokeniko
          : estjtPricePerGram * bullionWeight;

      const priceWithPercentage = basePrice * (1 + Number(percentageSum) / 100);

      setCutDigitBullionPrice(
        cutDigitFromRight(
          priceWithPercentage,
          !!formValues?.cutNumber ? formValues?.cutNumber : cutNumberDefault
        )
      );
    }
  }, [
    costPercentage,
    pricePerGram,
    calculatedNewBullionPrice,
    newBullionPrice,
    cutNumberDefault,
    profitPercentageDefault,
    formValues?.[percentageInputName],
    formValues?.cutNumber,
    formValues?.manualBasePrice,
    activeInput,
    appliedCutNumber,
  ]);

  useEffect(() => {
    if (activeInput === 'price' || activeInput === 'cutNumber') return;
  }, [activeInput]);

  const onSubmit = async data => {
    try {
      let formData = {};

      for (let key in data) {
        if (key === 'price') {
          data[key] = Number(cutDigitBullionPrice);
        }
        let value = data[key];

        // اصلاح این بخش برای عدم تبدیل متغیرهای باینری مانند سوئیچ به ساختارهای عددی اشتباه
        if (typeof data[key] === 'boolean') {
          value = data[key];
        } else if (!!isNumeric(data[key])) {
          value = !!String(data[key])?.includes(',')
            ? Number(data[key]?.replace(/,/g, ''))
            : Number(data[key]);
        }

        formData = { ...formData, [key]: value };
      }
      formData = {
        ...formData,
        calculateFloorPriceForSell: !formData.calculateFloorPriceForSell,
      };

      let unitPrice =
        strategy === 'MANUAL'
          ? !is995
            ? manualBasePrice
            : (manualBasePrice / 999) * 995
          : strategy === 'AUTOMATIC_ESTJT'
            ? estjtPricePerGram
            : competitorPrice?.[bullionWeight]?.tokeniko;
      await onClick({
        strategy: isPreview ? 'MANUAL' : strategy,
        transactionType,
        ...formData,
        unitPrice,
      });

      trig();
      strategy === 'MANUAL' && closeDialog();
    } catch (error) {
      console.log('error', error);
    }
  };

  const handleKeyDown = event => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      handleSubmit(onSubmit)();
    }
  };

  const handleConfirmFloorPrice = () => {
    setValue('calculateFloorPriceForSell', true, { shouldDirty: true });
    setOpenFloorPriceConfirm(false);
  };

  const handleCancelFloorPrice = () => {
    setOpenFloorPriceConfirm(false);
  };

  const isFloorPriceChecked = !!formValues?.calculateFloorPriceForSell;

  return (
    <DialogContent
      sx={{
        animation: `${spin} 400ms ease-out`,
        dir: 'rtl',
        backgroundColor: 'transparent',
        p: { xs: 2, sm: 3 },
      }}
    >
      {/* 1. HERO DASHBOARD (Compact Version) */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: '#ECFDF5',
          borderRadius: '20px',
          p: { xs: 2, sm: 2.5 },
          mb: 2.5,
          boxShadow: '0 8px 24px -6px rgba(16, 185, 129, 0.15)',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            top: -30,
            right: -30,
            width: 120,
            height: 120,
            borderRadius: '50%',
            background:
              'linear-gradient(135deg, rgba(16, 185, 129, 0.15), rgba(16, 185, 129, 0))',
            pointerEvents: 'none',
          }}
        />

        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 1,
            mb: 1.5,
            zIndex: 1,
          }}
        >
          <Stack
            flexDirection={'row'}
            alignItems={'center'}
            sx={{ direction: 'ltr' }}
            gap={1}
          >
            <Typography
              variant="overline"
              sx={{
                color: '#047857',
                fontWeight: 800,
                fontSize: '0.8rem',
                letterSpacing: '0.5px',
                lineHeight: 1,
              }}
            >
              استراتژی فعلی:
            </Typography>{' '}
            <Box
              sx={{
                bgcolor: '#047857',
                color: '#fff',
                px: 1,
                py: 0.25,
                borderRadius: '6px',
                fontSize: '0.7rem',
                fontWeight: 700,
              }}
            >
              {strategies[strategyDefault]}
            </Box>
          </Stack>
          <Typography
            variant="overline"
            sx={{
              color: '#047857',
              fontWeight: 800,
              fontSize: '0.8rem',
              letterSpacing: '0.5px',
              lineHeight: 1,
            }}
          >
            قیمت نهایی فروش
          </Typography>
        </Box>

        <Typography
          dir="rtl"
          sx={{
            fontSize: { xs: '1.75rem', sm: '2.25rem' },
            fontWeight: 800,
            color:
              manualPriceLessThanThreshhold || manualPriceMoreThanThreshhold
                ? '#E11D48'
                : '#065F46',
            lineHeight: 1.1,
            textShadow: '0 2px 8px rgba(6, 95, 70, 0.08)',
            zIndex: 1,
          }}
        >
          {Number(cutDigitBullionPrice).toLocaleString('fa-IR')}
          <Typography
            component="span"
            sx={{
              fontSize: '1rem',
              fontWeight: 700,
              ml: 1,
              color: '#059669',
              opacity: 0.8,
            }}
          >
            ریال
          </Typography>
        </Typography>

        <Typography
          dir="rtl"
          sx={{
            color: '#047857',
            opacity: 0.85,
            fontSize: '0.8rem',
            fontWeight: 500,
            mt: 1,
            zIndex: 1,
            textAlign: 'center',
          }}
        >
          {`شامل ${
            Number(percentageState) +
            (strategy === 'AUTOMATIC_ESTJT' ? Number(costPercentage) : 0)
          }٪ سود و ${Number(appliedCutNumber)} رقم گرد شده  `}
        </Typography>

        {isManualStrategy &&
          percentageState < 0 &&
          cutDigitBullionPrice != 0 &&
          Number(formValues?.boardPrice) !== cutDigitBullionPrice && (
            <Box
              sx={{
                direction: 'ltr',
                mt: 1.5,
                pt: 1.5,
                borderTop: '1px solid rgba(16, 185, 129, 0.2)',
                width: '100%',
                display: 'flex',
                justifyContent: 'space-between',
                zIndex: 1,
              }}
            >
              <Typography
                variant="body2"
                sx={{ color: '#065F46', fontWeight: 600, fontSize: '0.85rem' }}
              >
                قیمت تابلو:
              </Typography>
              <Typography
                variant="body2"
                sx={{ color: '#065F46', fontWeight: 800, fontSize: '0.85rem' }}
              >
                {(!!String(formValues?.boardPrice)?.includes(',')
                  ? Number(formValues?.boardPrice?.replace(/,/g, ''))
                  : Number(formValues?.boardPrice)
                ).toLocaleString('fa-IR')}{' '}
                <Typography component="span" sx={{ fontSize: '0.75rem' }}>
                  ریال
                </Typography>
              </Typography>
            </Box>
          )}
      </Box>

      {/* 2. MAIN FORM INPUTS */}
      <form onSubmit={handleSubmit(onSubmit)} onKeyDown={handleKeyDown}>
        <Box
          sx={{
            bgcolor: '#F8FAFC',
            borderRadius: '20px',
            p: { xs: 2, sm: 2.5 },
            mb: 3,
            border: '1px solid #E2E8F0',
          }}
        >
          <Stack gap={2}>
            {/* Strategy Select */}
            {!isPreview && (
              <>
                <Box>
                  <Typography
                    variant="subtitle2"
                    sx={{
                      direction: 'ltr',
                      color: '#334155',
                      mb: 1,
                      fontWeight: 700,
                      fontSize: '0.85rem',
                    }}
                  >
                    تنظیمات پایه
                  </Typography>
                  <Stack gap={2} flexDirection={'row'}>
                    {/* تعریف متغیر برای بررسی وضعیت فعال بودن سوئیچ */}

                    {!isPreview && strategy === 'MANUAL' && (
                      <Box
                        sx={{
                          flex: 1,
                          p: 1,
                          bgcolor: isFloorPriceChecked ? '#FEF2F2' : '#F1F5F9', // پس‌زمینه قرمز ملایم در حالت فعال
                          borderRadius: '12px',
                          border: isFloorPriceChecked
                            ? '1px dashed #FCA5A5'
                            : '1px dashed #E2E8F0', // حاشیه قرمز ملایم در حالت فعال
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          transition: 'all 0.2s ease-in-out',
                        }}
                      >
                        <Controller
                          name="calculateFloorPriceForSell"
                          control={control}
                          render={({ field: { value, onChange } }) => (
                            <Switch
                              checked={!!value}
                              onChange={e => {
                                const checked = e.target.checked;
                                if (checked) {
                                  setOpenFloorPriceConfirm(true);
                                } else {
                                  onChange(false);
                                }
                              }}
                              color="error"
                              size="small"
                            />
                          )}
                        />
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: 500,
                            color: isFloorPriceChecked ? '#991B1B' : '#334155', // متن قرمز تیره در حالت فعال
                            fontSize: '0.8rem',
                            transition: 'color 0.2s ease-in-out',
                          }}
                        >
                          نادیده گرفتن قیمت مرجع
                        </Typography>
                      </Box>
                    )}
                    <Controller
                      defaultValue={strategyDefault}
                      name="strategy"
                      control={control}
                      render={({ field }) => (
                        <FormControl
                          fullWidth
                          variant="filled"
                          size="small"
                          sx={{
                            flex: 1,
                            '& .MuiFilledInput-root': {
                              bgcolor: 'transparent',
                              '&:before, &:after': { display: 'none' },
                            },
                          }}
                        >
                          <Select
                            dir="rtl"
                            {...field}
                            onFocus={() => setActiveInput('strategy')}
                            onBlur={() => setActiveInput(null)}
                            displayEmpty
                            sx={{
                              borderRadius: '12px',
                              backgroundColor: '#FFFFFF',
                              boxShadow: '0 1px 4px rgba(0,0,0,0.02)',
                              border: '1px solid #E2E8F0',
                              transition: 'all 0.2s ease',
                              '& .MuiSelect-select': {
                                py: 1.2,
                                px: 1.5,
                                fontWeight: 700,
                                color: '#0F172A',
                                fontSize: '0.95rem',
                              },
                              '&:hover': {
                                borderColor: '#10B981',
                                boxShadow: '0 2px 8px rgba(16, 185, 129, 0.08)',
                              },
                              '&.Mui-focused': {
                                borderColor: '#10B981',
                                boxShadow: '0 0 0 3px rgba(16, 185, 129, 0.2)',
                              },
                              '& fieldset': { border: 'none' },
                            }}
                          >
                            <MenuItem
                              value="MANUAL"
                              sx={{ fontWeight: 600, fontSize: '0.9rem' }}
                            >
                              قیمت‌گذاری دستی
                            </MenuItem>
                            <MenuItem
                              value="AUTOMATIC_COMPETITOR"
                              sx={{ fontWeight: 600, fontSize: '0.9rem' }}
                            >
                              تنظیم بر اساس رقیب
                            </MenuItem>
                            <MenuItem
                              value="AUTOMATIC_ESTJT"
                              sx={{ fontWeight: 600, fontSize: '0.9rem' }}
                            >
                              تنظیم بر اساس مرجع قیمت
                            </MenuItem>
                          </Select>
                        </FormControl>
                      )}
                    />

                    {/* سوئیچ جدید محاسبه قیمت کف در زمان پیش‌نمایش */}
                  </Stack>
                </Box>
                <Divider sx={{ borderColor: '#E2E8F0' }} />
              </>
            )}

            {/* Calculations Fields */}
            <Box>
              <Typography
                variant="subtitle2"
                sx={{
                  direction: 'ltr',
                  color: '#334155',
                  mb: 1.5,
                  fontWeight: 700,
                  fontSize: '0.85rem',
                }}
              >
                پارامترهای محاسبه
              </Typography>

              <Stack gap={2}>
                {strategy === 'MANUAL' && (
                  <Controller
                    name="manualBasePrice"
                    control={control}
                    defaultValue={manualBasePrice}
                    disabled={isPreview}
                    render={({ field }) => (
                      <NumericFormat
                        {...field}
                        onFocus={() => setActiveInput('manualBasePrice')}
                        onBlur={() => setActiveInput(null)}
                        defaultValue={manualBasePrice}
                        customInput={TextField}
                        disabled={isPreview}
                        thousandSeparator=","
                        placeholder="قیمت هر گرم"
                        fullWidth
                        size="small"
                        label="قیمت هر گرم (ریال)"
                        autoComplete="off"
                        inputProps={{ inputMode: 'numeric' }}
                        decimalScale={0}
                        allowNegative={false}
                        sx={premiumInputSx}
                        InputLabelProps={{ shrink: true }}
                      />
                    )}
                  />
                )}

                <Stack direction={{ xs: 'column', sm: 'row' }} gap={2}>
                  <Controller
                    name={
                      isPreview
                        ? 'manualMarginPercentage'
                        : marginPercentages[strategy]?.value || ''
                    }
                    control={control}
                    defaultValue={profitPercentageDefault}
                    render={({ field }) => (
                      <NumericFormat
                        {...field}
                        onFocus={() =>
                          setActiveInput(
                            isPreview
                              ? 'manualMarginPercentage'
                              : marginPercentages[strategy]?.value || ''
                          )
                        }
                        onBlur={() => setActiveInput(null)}
                        defaultValue={profitPercentageDefault}
                        customInput={TextField}
                        placeholder={
                          isPreview
                            ? 'درصد سود'
                            : marginPercentages[strategy]?.label || ''
                        }
                        fullWidth
                        size="small"
                        label={
                          isPreview
                            ? 'درصد سود'
                            : marginPercentages[strategy]?.label || ''
                        }
                        autoComplete="off"
                        inputProps={{ inputMode: 'numeric' }}
                        sx={premiumInputSx}
                        InputLabelProps={{ shrink: true }}
                      />
                    )}
                  />

                  <Controller
                    name="cutNumber"
                    control={control}
                    defaultValue={cutNumberDefault}
                    render={({ field }) => (
                      <NumericFormat
                        {...field}
                        onFocus={() => setActiveInput('cutNumber')}
                        onBlur={() => setActiveInput(null)}
                        defaultValue={cutNumberDefault}
                        customInput={TextField}
                        thousandSeparator=","
                        placeholder="رقم گرد کردن"
                        fullWidth
                        size="small"
                        label="رقم گرد کردن"
                        autoComplete="off"
                        inputProps={{ inputMode: 'numeric' }}
                        decimalScale={0}
                        allowNegative={false}
                        sx={premiumInputSx}
                        InputLabelProps={{ shrink: true }}
                      />
                    )}
                  />
                </Stack>
              </Stack>
            </Box>
          </Stack>
        </Box>

        {/* 3. ACTIONS */}
        <Box sx={{ px: { xs: 0, sm: 1 } }}>
          <ResponsiveDialogActions
            onAccept={handleSubmit(onSubmit)}
            onReject={closeDialog}
            dangerAcceptCall={cutDigitBullionPrice == '0'}
            acceptText={
              manualPriceLessThanThreshhold && cutDigitBullionPrice != '0'
                ? 'کمتر از حدمجاز'
                : manualPriceMoreThanThreshhold
                  ? 'بیشتر از حدمجاز'
                  : isSubmitting
                    ? 'در حال ذخیره...'
                    : cutDigitBullionPrice == '0'
                      ? 'توقف فروش'
                      : 'تایید و ذخیره'
            }
            acceptDisable={
              !isDirty ||
              isSubmitting ||
              (manualPriceLessThanThreshhold && cutDigitBullionPrice != '0') ||
              manualPriceMoreThanThreshhold
            }
            acceptButtonProps={{
              sx: {
                py: 1.2,
                borderRadius: '12px',
                background: cutDigitBullionPrice == '0' ? '#E11D48' : '#10B981',
                color: '#fff',
                fontWeight: 700,
                fontSize: '0.95rem',
                letterSpacing: '0.5px',
                boxShadow:
                  cutDigitBullionPrice == '0'
                    ? '0 6px 16px rgba(225, 29, 72, 0.2)'
                    : '0 6px 16px rgba(16, 185, 129, 0.2)',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                '&:hover': {
                  background:
                    cutDigitBullionPrice == '0' ? '#BE123C' : '#059669',
                  transform:
                    isDirty && !isSubmitting ? 'translateY(-2px)' : 'none',
                  boxShadow:
                    cutDigitBullionPrice == '0'
                      ? '0 8px 20px rgba(225, 29, 72, 0.3)'
                      : '0 8px 20px rgba(16, 185, 129, 0.3)',
                },
                '&:disabled': {
                  background: '#CBD5E1',
                  color: '#94A3B8',
                  boxShadow: 'none',
                },
              },
            }}
            rejectButtonProps={{
              sx: {
                py: 1.2,
                borderRadius: '12px',
                border: '2px solid transparent',
                color: '#64748B',
                fontWeight: 700,
                fontSize: '0.95rem',
                backgroundColor: '#F1F5F9',
                transition: 'all 0.3s ease',
                '&:hover': {
                  backgroundColor: '#FEE2E2',
                  color: '#E11D48',
                  transform: 'translateY(-2px)',
                },
              },
            }}
          />
        </Box>
      </form>

      {/* دکمه تایید نادیده گرفتن قیمت مرجع */}
      <ConfirmFloorPriceDialog
        open={openFloorPriceConfirm}
        onClose={handleCancelFloorPrice}
        onConfirm={handleConfirmFloorPrice}
        title="تایید نادیده گرفتن قیمت مرجع"
        message="این تغییر بدون بررسی مرجع قیمت‌گذاری انجام می‌شود و دارای ریسک مالی است. آیا مطمئن هستید؟"
      />
    </DialogContent>
  );
}
